/**
 * Produktmodelle -- Liste und Detail.
 *
 * Die interne Produktliste zeigt eine Zeile je PRODUKTMODELL, nicht je SKU
 * (PRODUCT_DATA_MODEL.md 23.1). Das ist der sichtbare Unterschied zur
 * Artikelliste: dort steht die kaufmaennische Einheit, hier die technische
 * Plattform.
 *
 * Das Detail ist seit Produktmodelle 002 die Modellseite V2 (`ModelPage.tsx`):
 * Kopf mit Kennzahlen, Reiter Uebersicht, Gemeinsame Daten, Ausfuehrungen,
 * Texte & Dokumente, Verlauf.
 *
 * Ausdruecklich NICHT vorhanden: Bestand, Preis, Bestellung und Liefertermin
 * auf Modellebene. Ein Produktmodell besitzt keinen ERP-Bestand (22.8) -- und
 * eine Oberflaeche, die dafuer ein Feld zeigte, waere bereits die falsche
 * Aussage.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CategoryItem, FamilyItem, MetaResponse, ProductModelDetail, ProductModelListItem,
} from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { modelLabel } from "../i18n/terminology.ts";
import { errorText } from "../i18n/errors.ts";
import { categoryLabel, familyLabel } from "../i18n/terminology.ts";
import { EmptyState, ErrorState, LoadingState } from "./States.tsx";
import { NeueFamilie, NeueHauptkategorie } from "./ArticleCreate.tsx";
import { ModelDetailPanel } from "./ModelPage.tsx";
import { gemerkteListenbreite, Splitter } from "./Splitter.tsx";
import type { AusfuehrungsKontext } from "./ModelAssign.tsx";
import "./ProductModels.css";

export function ProductModels({ meta, mayCreateCategory, mayWriteModels, mayWriteLogistics, mayWriteTexts, mayReleaseTexts, mayWriteProps, mayConfirmProps, mayWriteDocuments, mayCreateArticle, mayDeleteModels, reloadToken, onNeuerArtikel, openId, onOpen, onOpenArticle }: {
  meta: MetaResponse;
  /** article.write -- neuer Artikel aus einer Ausfuehrung (Produktmodelle 002A). */
  mayCreateArticle: boolean;
  /**
   * `system.manage` -- die strukturellen Setzungen: eine neue
   * Hauptkategorie im Anlageflow (UI/UX 001) und die Pflege des
   * Merkmalskatalogs (Produktmodelle 003 V2). Beide gliedern den ganzen
   * Katalog; deshalb dieselbe Schwelle und dasselbe Kennzeichen.
   */
  mayCreateCategory: boolean;
  /** model.write UND article.delete -- endgueltiges Loeschen eines Modells. */
  mayDeleteModels: boolean;
  /** Steigt, wenn das offene Modell von aussen neu zu laden ist (nach einer Anlage). */
  reloadToken: number;
  onNeuerArtikel: (k: AusfuehrungsKontext) => void;
  /** document.write -- Modelldokumente auf der Modellseite (Produktmodelle 002). */
  mayWriteDocuments: boolean;
  mayWriteModels: boolean;
  mayWriteLogistics: boolean;
  mayWriteTexts: boolean;
  mayReleaseTexts: boolean;
  mayWriteProps: boolean;
  mayConfirmProps: boolean;
  openId: number | null;
  onOpen: (id: number | null) => void;
  onOpenArticle: (id: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [items, setItems] = useState<readonly ProductModelListItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [creating, setCreating] = useState(false);
  /*
   * DIESELBE TRENNLEISTE WIE IN DER PRODUKTLISTE (PM003-UI-01).
   *
   * Die Modellansicht uebernahm `app__split--detail` von der
   * Artikelansicht, aber nicht deren ziehbare Leiste: die Liste blieb auf
   * ihrer Vorgabespalte stehen, waehrend sie nebenan verschiebbar ist.
   * Hier steht kein eigenes Verhalten, sondern dieselbe Komponente mit
   * demselben gemerkten Mass -- eine Vorliebe dieses Bildschirms gilt fuer
   * beide Listen, nicht je Ansicht eine andere.
   */
  const splitRef = useRef<HTMLDivElement | null>(null);
  const [listeBreite, setListeBreite] = useState<number | null>(() => gemerkteListenbreite());

  const load = useCallback(async () => {
    setError(null);
    try { setItems((await api.models()).items); }
    catch (e) { setError(e); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items ?? []).filter((m) => {
      if (categoryId && m.category.id !== categoryId) return false;
      if (!q) return true;
      return [m.code, m.name, m.family.name].some((v) => v.toLowerCase().includes(q));
    });
  }, [items, search, categoryId]);

  if (error !== null && !items) {
    return <div className="list models"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!items) return <div className="list models"><LoadingState label={t("models.loading")} /></div>;

  return (
    /*
     * DER DETAILMODUS MUSS AM SPLIT STEHEN, SONST IST DAS DETAIL EIN STRICH.
     *
     * `.app__split` ist eine Flex-Zeile, in der die Liste mit `flex: 1` allen
     * Platz beansprucht. Das Detail traegt `container-type: inline-size` --
     * damit ist seine Inline-Groesse eingeschlossen, seine Inhaltsbreite
     * rechnet als 0, und ohne eigenen Wachstumsanteil bleibt es genau dort.
     * Gemessen am 12.09.2026: 1 px breit an Position 1399 in einem 1400 px
     * breiten Fenster -- im DOM vorhanden, mit allen Schaltflaechen, und fuer
     * einen Menschen unsichtbar. Ein Klick auf eine Modellzeile schien
     * wirkungslos.
     *
     * Erst `app__split--detail` gibt der Liste eine feste Spalte und dem
     * Detail den Rest. Die Artikelansicht setzt das seit jeher; hier fehlte
     * es. Dieselbe Zeile, dieselbe Bedingung.
     */
    <div className={openId !== null ? "app__split app__split--detail" : "app__split"}
      ref={splitRef}
      style={listeBreite !== null
        ? { ["--liste-breite" as string]: `${listeBreite}px` } as React.CSSProperties : undefined}>
      <div className="list models">
        <div className="list__toolbar">
          <input className="list__search" placeholder={t("models.searchPlaceholder")}
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 aria-label={t("models.search")} />
          <select className="list__select" value={categoryId ?? ""} aria-label={t("list.category")}
                  onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">{t("list.allCategories")}</option>
            {meta.categories.map((c) => (
              <option key={c.id} value={c.id}>{categoryLabel(i18n, c.code, c.name)}</option>
            ))}
          </select>
          {(search || categoryId) && (
            <button className="btn list__reset" onClick={() => { setSearch(""); setCategoryId(undefined); }}>
              {t("list.resetFilters")}
            </button>
          )}
          {mayWriteModels && (
            <button className="btn btn--primary" onClick={() => setCreating(true)}>{t("models.create")}</button>
          )}
        </div>

        <div className="list__meta">
          <span><strong>{shown.length}</strong> {t("models.countLabel")}</span>
        </div>

        {creating && (
          <CreateModelForm meta={meta} mayCreateCategory={mayCreateCategory}
            onDone={(created) => {
              setCreating(false);
              if (created) { void load(); onOpen(created.id); }
            }}
            onError={setError} />
        )}

        {shown.length === 0 ? (
          <EmptyState title={t("models.emptyTitle")} hint={t("models.emptyHint")} />
        ) : (
          <div className="list__table">
            <table>
              <thead>
                <tr>
                  <th>{t("models.colModel")}</th>
                  <th>{t("models.colFamily")}</th>
                  {/* Bestellbar und davon materialisiert -- die Zahlen, die
                      die Mischmaterialisierung sichtbar machen (23.1). */}
                  <th className="models__num">{t("models.colVariants")}</th>
                  <th className="models__num">{t("models.colMaterialised")}</th>
                  {/*
                    * DIE SPALTE „FOKUS" IST FORT (Produktmodelle 003 V2).
                    *
                    * `focusRank` ist die Fokusvariante eines Modells -- ein
                    * fachlich richtiges Feld, das heute niemand setzt: in
                    * jeder Zeile stand 0. Eine Spalte, die ueberall
                    * dasselbe sagt, kostet Blick und gibt nichts. Das FELD
                    * bleibt (Vertrag und Datenbank unveraendert); sobald es
                    * gepflegt wird, kann die Spalte zurueckkommen -- dann
                    * mit einem Namen, der sagt, was sie meint.
                    */}
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => (
                  <tr key={m.id} tabIndex={0}
                      className={openId === m.id ? "models__row--open" : ""}
                      aria-current={openId === m.id ? "true" : undefined}
                      onClick={() => onOpen(m.id)}
                      onKeyDown={(e) => { if (e.key === "Enter") onOpen(m.id); }}>
                    <td className="cell-number">
                      {/*
                        * ── EINMAL, WENN BEIDE DASSELBE SAGEN (§3) ──
                        *
                        * Hier standen Code UND Name nebeneinander,
                        * bedingungslos. Bei HB200-U tragen beide
                        * denselben Wert, und die Liste zeigte
                        * „HB200-U  HB200-U".
                        *
                        * DIESELBE FUNKTION WIE IN DER ARTIKELANSICHT.
                        * Eine zweite Regel daneben waere die zweite
                        * Wahrheit, und eine davon waere irgendwann
                        * die aeltere.
                        */}
                      {(() => {
                        const l = modelLabel(m.code, m.name);
                        return <>
                          <code>{l.code}</code>
                          {l.name !== null && <span className="models__name">{l.name}</span>}
                        </>;
                      })()}
                      {/* Ein regelbasiert vorgeschlagenes Modell ist keine
                          fachliche Setzung -- und sagt das auch. */}
                      {m.provenance !== "bestaetigt" && (
                        <span className="badge badge--draft" title={t("models.unconfirmedTitle")}>
                          {t("models.unconfirmed")}
                        </span>
                      )}
                    </td>
                    <td className="cell-cat">{familyLabel(i18n, m.family.code, m.family.name)}</td>
                    <td className="models__num">{m.variantCount}</td>
                    <td className="models__num">{m.materialisedCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
      </div>

      {openId !== null && (
        <Splitter container={splitRef} breite={listeBreite} onBreite={setListeBreite} />
      )}
      {openId !== null && (
        <ModelDetailPanel key={`${openId}-${reloadToken}`} id={openId} meta={meta}
          rights={{ mayWriteModels, mayWriteLogistics, mayWriteTexts, mayReleaseTexts,
                    mayWriteProps, mayConfirmProps, mayWriteDocuments, mayCreateArticle, mayDeleteModels,
                    mayManageCatalog: mayCreateCategory }}
          onNeuerArtikel={onNeuerArtikel}
          onDeleted={() => { onOpen(null); void load(); }}
          onClose={() => onOpen(null)} onListChanged={load} onOpenArticle={onOpenArticle} />
      )}
    </div>
  );
}

/**
 * Anlegen eines Produktmodells.
 *
 * Drei Angaben, mehr nicht: Code, Name und Produktfamilie. Ein Modell gehoert
 * zu genau einer Familie -- und die Zuordnung von Artikeln ist ein eigener,
 * ausdruecklicher Schritt danach. Es gibt hier keinen Weg, der Artikel
 * automatisch mitgruppiert.
 */
function CreateModelForm({ meta, mayCreateCategory, onDone, onError }: {
  meta: MetaResponse;
  /** system.manage -- eine elfte Hauptkategorie ist eine strukturelle Setzung. */
  mayCreateCategory: boolean;
  onDone: (created: ProductModelDetail | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  /* Das EINZIGE mehrzeilige Feld dieser Anlage -- und es ist optional.
     Dieselbe Spalte, die die Stammdatenpflege schon kennt (`note`), damit
     Anlage und Bearbeitung nicht zwei verschiedene Modelle zeigen. */
  const [note, setNote] = useState("");
  const [familyId, setFamilyId] = useState<number | "">("");
  /*
   * DIE EINORDNUNG STEHT VORN -- und zwar in der Reihenfolge, in der HOMEBASE
   * denkt: HAUPTKATEGORIE -> PRODUKTFAMILIE -> PRODUKTMODELL (UI/UX 001).
   *
   * Bis hierher standen Modellcode und Modellname oben und die Einordnung
   * darunter. Das las sich wie „erfinde einen Code, dann sag, wo er
   * hingehoert" -- die umgekehrte Denkrichtung. Die Kategorie grenzt
   * ausserdem die Familienliste ein: 36 Familien in einem Auswahlfeld sind
   * eine Suche, zehn Kategorien sind eine Entscheidung. Und eine neu
   * angelegte Familie BRAUCHT ihre Kategorie.
   *
   * Die Familie bleibt deshalb gesperrt, solange keine Kategorie steht --
   * sichtbar gesperrt, nicht heimlich leer.
   */
  const [categoryId, setCategoryId] = useState<string>("");
  const [neu, setNeu] = useState<FamilyItem | null>(null);
  /* Eine eben angelegte Kategorie steht in `meta` noch nicht. */
  const [neueKategorie, setNeueKategorie] = useState<CategoryItem | null>(null);

  /*
   * ANGEBOTEN WIRD, WAS AKTIV IST (Lifecycle 001).
   *
   * Archivierte und weggeraeumte Strukturen bleiben ueberall sichtbar, wo
   * bestehende Daten sie tragen -- aber eine NEUANLAGE haengt man nicht an
   * eine stillgelegte Ordnung. Das ist der ganze Unterschied zwischen
   * Archivieren und Loeschen, und er entscheidet sich genau hier.
   */
  const aktiv = <T extends { archived?: boolean; trashed?: boolean }>(xs: readonly T[]): T[] =>
    xs.filter((x) => x.archived !== true && x.trashed !== true);

  const kategorien = useMemo(() => (
    neueKategorie && !meta.categories.some((c) => c.id === neueKategorie.id)
      ? [...aktiv(meta.categories), neueKategorie] : aktiv(meta.categories)
  ), [meta.categories, neueKategorie]);

  /* Die Familien der gewaehlten Kategorie -- plus die eben angelegte, die
     in `meta` noch nicht steht. */
  const familien = useMemo(() => {
    if (categoryId === "") return [];
    const aus = aktiv(meta.families).filter((f) => f.categoryId === Number(categoryId));
    return neu && !aus.some((f) => f.id === neu.id) ? [...aus, neu] : aus;
  }, [meta.families, categoryId, neu]);

  async function save(): Promise<void> {
    if (!code.trim() || !name.trim() || familyId === "") return;
    try {
      onDone(await api.createModel({
        code: code.trim(), name: name.trim(), familyId: Number(familyId),
        ...(note.trim() === "" ? {} : { note: note.trim() }),
      }));
    }
    catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="models__form models__form--wide" data-testid="ux-modellanlage">
      <div className="feldgruppe" data-testid="ux-einordnung">
        <h4 className="feldgruppe__titel">{t("models.sectionPlacement")}</h4>
        <div className="feldzeile">
          <label className="feld feld--kurz">
            <span className="feld__label">{t("models.category")}</span>
            <select className="feld__select" data-testid="ux-kategorie" value={categoryId}
                    onChange={(e) => { setCategoryId(e.target.value); setFamilyId(""); }}>
              <option value="">{t("models.choose")}</option>
              {kategorien.map((c) => (
                <option key={c.id} value={c.id}>{categoryLabel(i18n, c.code, c.name)}</option>
              ))}
            </select>
          </label>
          <label className="feld feld--kurz">
            <span className="feld__label">{t("models.colFamily")}</span>
            <select className="feld__select" data-testid="ux-familie" value={familyId}
                    disabled={categoryId === ""}
                    onChange={(e) => setFamilyId(e.target.value === "" ? "" : Number(e.target.value))}>
              <option value="">{categoryId === "" ? t("models.familyAfterCategory") : t("models.choose")}</option>
              {familien.map((f) => (
                <option key={f.id} value={f.id}>{familyLabel(i18n, f.code, f.name)}</option>
              ))}
            </select>
          </label>
        </div>
        {/* KEINE SACKGASSE, WENN KATEGORIE ODER FAMILIE FEHLEN. Beide Anlagen
            stehen hier, wo sie gebraucht werden -- und beide nur mit Recht. */}
        <div className="models__inline">
          {mayCreateCategory && <NeueHauptkategorie i18n={i18n}
            onAngelegt={(c) => { setNeueKategorie(c); setCategoryId(String(c.id)); setFamilyId(""); }} />}
          <NeueFamilie categoryId={categoryId} i18n={i18n}
            onAngelegt={(f) => { setNeu(f); setFamilyId(f.id); }} />
        </div>
      </div>

      <div className="feldgruppe" data-testid="ux-modelldaten">
        <h4 className="feldgruppe__titel">{t("models.sectionModel")}</h4>
        <div className="feldzeile">
          <label className="feld feld--kurz">
            <span className="feld__label">{t("models.code")}</span>
            <input className="feld__input" data-testid="ux-modellcode" value={code} maxLength={60}
                   onChange={(e) => setCode(e.target.value)} />
            <small className="feld__note">{t("models.codeHint")}</small>
          </label>
          <label className="feld">
            <span className="feld__label">{t("models.name")}</span>
            <input className="feld__input" data-testid="ux-modellname" value={name} maxLength={160}
                   onChange={(e) => setName(e.target.value)} />
            <small className="feld__note">{t("models.nameHint")}</small>
          </label>
        </div>
        <label className="feld feld--lang">
          <span className="feld__label">{t("mp.note")}</span>
          <textarea className="feld__input feld__mehrzeilig" data-testid="ux-modellnotiz" rows={2}
                    value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p className="feld__note">{t("models.textsLater")}</p>
      </div>

      <div className="models__formactions">
        <button className="btn btn--primary" onClick={save}
                disabled={!code.trim() || !name.trim() || familyId === ""}>{t("common.save")}</button>
        <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}
